/**
 * Newsletter Step 2 Component
 * Personal details and terms acceptance
 */

import { useState } from 'react';
import { User, Mail, ChevronLeft, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { dataLayerPushClickButton, dataLayerPushClickFieldTextArea } from '@/lib/ga4';

/** GA4 `Click_Field_Text_Area` form name for the newsletter sign-up modal. */
const NEWSLETTER_FORM = 'Newsletter Form';
import { useNewsletter } from '../hooks/useNewsletter';
import { MODAL_CONTENT, VALIDATION_MESSAGES } from '../constants';

interface FormErrors {
  firstName?: string;
  lastName?: string;
  confirmEmail?: string;
  terms?: string;
}

export function NewsletterStep2() {
  const { formData, updateFormData, prevStep, submit, isSubmitting, error } = useNewsletter();
  const [errors, setErrors] = useState<FormErrors>({});

  const content = MODAL_CONTENT.steps.step2;

  const validateAndSubmit = async () => {
    const newErrors: FormErrors = {};

    // Validate first name
    if (!formData.firstName.trim()) {
      newErrors.firstName = VALIDATION_MESSAGES.firstNameRequired;
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = VALIDATION_MESSAGES.firstNameMinLength;
    }

    // Validate last name
    if (!formData.lastName.trim()) {
      newErrors.lastName = VALIDATION_MESSAGES.lastNameRequired;
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = VALIDATION_MESSAGES.lastNameMinLength;
    }

    // Validate confirm email
    if (formData.confirmEmail !== formData.email) {
      newErrors.confirmEmail = VALIDATION_MESSAGES.emailMismatch;
    }

    // Validate terms
    if (!formData.termsAccepted) {
      newErrors.terms = VALIDATION_MESSAGES.termsRequired;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    await submit();
  };

  const clearError = (field: keyof FormErrors) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 tablet:px-6 tablet:py-6 space-y-4">
        {/* Title */}
        <div>
          <h3 className="text-base tablet:text-lg font-bold text-gray-900">
            {content.title}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{content.subtitle}</p>
        </div>

        {/* First name */}
        <div>
          <label htmlFor="newsletter-firstname" className="block text-sm font-medium text-gray-700 mb-1.5">
            {content.firstNameLabel}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="newsletter-firstname"
              type="text"
              value={formData.firstName}
              onChange={(e) => {
                updateFormData({ firstName: e.target.value });
                clearError('firstName');
              }}
              onFocus={(e) => dataLayerPushClickFieldTextArea(e, 'First Name', NEWSLETTER_FORM)}
              placeholder={content.firstNamePlaceholder}
              aria-required={true}
              aria-invalid={errors.firstName ? true : undefined}
              aria-describedby={errors.firstName ? 'newsletter-firstname-error' : undefined}
              className={cn(
                'w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm',
                'transition-all duration-200',
                'focus:outline-none focus:ring-0',
                errors.firstName
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-[#014383] hover:border-gray-300'
              )}
            />
          </div>
          {errors.firstName && (
            <p id="newsletter-firstname-error" role="alert" className="mt-1.5 text-xs text-red-600">{errors.firstName}</p>
          )}
        </div>

        {/* Last name */}
        <div>
          <label htmlFor="newsletter-lastname" className="block text-sm font-medium text-gray-700 mb-1.5">
            {content.lastNameLabel}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="newsletter-lastname"
              type="text"
              value={formData.lastName}
              onChange={(e) => {
                updateFormData({ lastName: e.target.value });
                clearError('lastName');
              }}
              onFocus={(e) => dataLayerPushClickFieldTextArea(e, 'Surname', NEWSLETTER_FORM)}
              placeholder={content.lastNamePlaceholder}
              aria-required={true}
              aria-invalid={errors.lastName ? true : undefined}
              aria-describedby={errors.lastName ? 'newsletter-lastname-error' : undefined}
              className={cn(
                'w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm',
                'transition-all duration-200',
                'focus:outline-none focus:ring-0',
                errors.lastName
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-[#014383] hover:border-gray-300'
              )}
            />
          </div>
          {errors.lastName && (
            <p id="newsletter-lastname-error" role="alert" className="mt-1.5 text-xs text-red-600">{errors.lastName}</p>
          )}
        </div>

        {/* Confirm email */}
        <div>
          <label htmlFor="newsletter-confirm-email" className="block text-sm font-medium text-gray-700 mb-1.5">
            {content.confirmEmailLabel}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="newsletter-confirm-email"
              type="email"
              value={formData.confirmEmail}
              onChange={(e) => {
                updateFormData({ confirmEmail: e.target.value });
                clearError('confirmEmail');
              }}
              onFocus={(e) => dataLayerPushClickFieldTextArea(e, 'Confirm Email', NEWSLETTER_FORM)}
              placeholder={content.confirmEmailPlaceholder}
              aria-required={true}
              aria-invalid={errors.confirmEmail ? true : undefined}
              aria-describedby={errors.confirmEmail ? 'newsletter-confirm-email-error' : undefined}
              className={cn(
                'w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm',
                'transition-all duration-200',
                'focus:outline-none focus:ring-0',
                errors.confirmEmail
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-[#014383] hover:border-gray-300'
              )}
            />
          </div>
          {errors.confirmEmail && (
            <p id="newsletter-confirm-email-error" role="alert" className="mt-1.5 text-xs text-red-600">{errors.confirmEmail}</p>
          )}
        </div>

        {/* Terms checkbox */}
        <div className="pt-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <div className="mt-0.5">
              <input
                type="checkbox"
                checked={formData.termsAccepted}
                onChange={(e) => {
                  updateFormData({ termsAccepted: e.target.checked });
                  clearError('terms');
                }}
                className="sr-only"
              />
              <div
                className={cn(
                  'w-5 h-5 rounded border-2 flex items-center justify-center',
                  'transition-all duration-200',
                  formData.termsAccepted
                    ? 'border-[#014383] bg-[#014383]'
                    : errors.terms
                      ? 'border-red-300'
                      : 'border-gray-300 hover:border-gray-400'
                )}
              >
                {formData.termsAccepted && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                    <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-gray-600">
              {content.termsLabel}{' '}
              <a
                href={content.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#014383] hover:underline font-medium"
              >
                {content.termsLink}
              </a>
              {' '}και την{' '}
              <a
                href={content.privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#014383] hover:underline font-medium"
              >
                {content.privacyLink}
              </a>
            </span>
          </label>
          {errors.terms && (
            <p role="alert" className="mt-1.5 text-xs text-red-600">{errors.terms}</p>
          )}
        </div>

        {/* API error */}
        {error && (
          <div role="alert" className="p-3 rounded-xl bg-red-50 border border-red-200">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-5 py-4 tablet:px-6 border-t border-gray-100 bg-gray-50/50">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              dataLayerPushClickButton(content.backButton, 'Back');
              prevStep();
            }}
            disabled={isSubmitting}
            className={cn(
              'px-4 py-3 rounded-xl font-medium text-sm',
              'text-gray-600 hover:text-gray-800 hover:bg-gray-100',
              'transition-colors duration-200 flex items-center gap-2',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <ChevronLeft className="h-4 w-4" />
            {content.backButton}
          </button>
          <button
            type="button"
            onClick={() => {
              dataLayerPushClickButton(content.submitButton, 'Submit Newsletter');
              validateAndSubmit();
            }}
            disabled={isSubmitting}
            className={cn(
              'flex-1 px-4 py-3 rounded-xl font-bold text-sm',
              'bg-gradient-to-r from-[#014383] to-[#0159a8] text-white',
              'hover:from-[#003d7a] hover:to-[#014383]',
              'shadow-lg shadow-[#014383]/25 hover:shadow-xl',
              'active:scale-[0.98] transition-all duration-200',
              'flex items-center justify-center gap-2',
              'disabled:opacity-70 disabled:cursor-not-allowed'
            )}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Αποστολή...
              </>
            ) : (
              content.submitButton
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
